/**
 * ─── Arena Members & Bets Pages ─────────────────────────────
 * Membership management + Bet placement.
 */

import { useEffect, useState, useCallback } from 'react'
import { Card, CardHeader, Badge, Button, Input, Modal } from '@repo/ui-core'
import { arenaMemberRepo, arenaBetRepo, arenaMatchRepo } from '../../lib/db'
import { MEMBERSHIP_TIER_LABELS, MEMBER_STATUS_LABELS, BET_STATUS_LABELS, BET_TYPE_LABELS } from '@repo/entity-arena'
import { Users, Plus, Coins, Pencil, Trash2 } from 'lucide-react'

// ═══════════════════════════════════════════════════════════════
// Members
// ═══════════════════════════════════════════════════════════════

export function ArenaMemberPage() {
  const [members, setMembers] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [editId, setEditId] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState({ firstName: '', lastName: '', phone: '', email: '', address: '',
    membershipTier: 'regular', notes: '' })

  const load = useCallback(async () => {
    setLoading(true)
    try { const r = await arenaMemberRepo.findMany({ page: 1, pageSize: 500 }); setMembers('items' in r ? r.items : []) }
    catch (e) { console.error(e) } finally { setLoading(false) }
  }, [])
  useEffect(() => { load() }, [load])

  async function save() {
    setSaving(true)
    try {
      const payload: any = { ...form, fullName: `${form.firstName} ${form.lastName}`.trim(),
        memberCode: 'MEM-' + Date.now().toString(36).toUpperCase().slice(-6),
        joinDate: Date.now(), expiryDate: Date.now() + 365 * 86400000,
        totalBetsPlaced: 0, totalWinnings: 0, totalEntrances: 0, status: 'active',
        tenantId: 'default', createdBy: 'admin', updatedBy: 'admin' }
      if (editId) await arenaMemberRepo.update(editId, { ...payload, updatedBy: 'admin' })
      else await arenaMemberRepo.create(payload)
      setShowModal(false); reset(); await load()
    } catch (e) { console.error(e) } finally { setSaving(false) }
  }

  function reset() { setForm({ firstName: '', lastName: '', phone: '', email: '', address: '', membershipTier: 'regular', notes: '' }); setEditId(null) }
  function edit(m: any) { setForm({ firstName: m.firstName, lastName: m.lastName, phone: m.phone, email: m.email ?? '', address: m.address ?? '', membershipTier: m.membershipTier, notes: m.notes ?? '' }); setEditId(m.id); setShowModal(true) }
  async function del(id: string) { if (!confirm('Delete?')) return; try { await arenaMemberRepo.delete(id); await load() } catch (e) { console.error(e) } }

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <div><h2 className="text-2xl font-bold text-gray-900">Arena Members</h2><p className="text-sm text-gray-500 mt-1">{members.length} members</p></div>
        <Button onClick={() => { reset(); setShowModal(true) }} icon={<Plus className="h-4 w-4" />}>Add Member</Button>
      </div>
      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {[['Total', members.length, 'blue'],['Active',members.filter((m:any)=>m.status==='active').length,'green'],['VIP',members.filter((m:any)=>m.membershipTier==='vip'||m.membershipTier==='platinum').length,'purple'],['Total Wagered',`₱${members.reduce((s:number,m:any)=>s+(m.totalBetsPlaced??0),0).toLocaleString()}`,'amber']].map(([l,v,c]:any)=>(
          <div key={l} className={`rounded-xl border border-${c}-200 bg-${c}-50 p-4`}><p className={`text-xs text-${c}-600`}>{l}</p><p className={`mt-1 text-xl font-bold text-${c}-700`}>{v}</p></div>
        ))}
      </div>
      <Card><CardHeader title="Membership Roster" />
        <div className="overflow-x-auto"><table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50"><tr>{['Code','Name','Phone','Tier','Status','Joined',''].map(h=><th key={h} className="px-3 py-2.5 text-left text-xs font-medium uppercase text-gray-500">{h}</th>)}</tr></thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {loading ? <tr><td colSpan={7} className="py-8 text-center text-gray-400">Loading...</td></tr> :
             members.length === 0 ? <tr><td colSpan={7} className="py-8 text-center text-gray-400">No members.</td></tr> :
             members.map((m:any)=>(
              <tr key={m.id} className="hover:bg-gray-50">
                <td className="px-3 py-2.5 font-mono text-xs">{m.memberCode}</td>
                <td className="px-3 py-2.5 font-medium">{m.fullName}</td>
                <td className="px-3 py-2.5 text-gray-500">{m.phone}</td>
                <td className="px-3 py-2.5"><Badge color={m.membershipTier==='vip'?'purple':m.membershipTier==='gold'?'yellow':'blue'}>{MEMBERSHIP_TIER_LABELS[m.membershipTier as keyof typeof MEMBERSHIP_TIER_LABELS]??m.membershipTier}</Badge></td>
                <td className="px-3 py-2.5"><Badge color={m.status==='active'?'green':'red'}>{MEMBER_STATUS_LABELS[m.status as keyof typeof MEMBER_STATUS_LABELS]??m.status}</Badge></td>
                <td className="px-3 py-2.5 text-xs text-gray-500">{new Date(m.joinDate).toLocaleDateString()}</td>
                <td className="px-3 py-2.5"><div className="flex gap-1">
                  <button onClick={()=>edit(m)} className="rounded p-1 hover:bg-gray-100"><Pencil className="h-3.5 w-3.5 text-blue-600"/></button>
                  <button onClick={()=>del(m.id)} className="rounded p-1 hover:bg-gray-100"><Trash2 className="h-3.5 w-3.5 text-red-600"/></button>
                </div></td>
              </tr>
            ))}
          </tbody>
        </table></div>
      </Card>
      <Modal open={showModal} onClose={()=>{setShowModal(false);reset()}} title={editId?'Edit Member':'Add Member'} size="md"
        footer={<div className="flex gap-2"><Button variant="secondary" onClick={()=>{setShowModal(false);reset()}}>Cancel</Button><Button onClick={save} loading={saving} icon={<Users className="h-4 w-4"/>}>{editId?'Update':'Create'}</Button></div>}>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Input label="First Name" value={form.firstName} onChange={e=>setForm({...form,firstName:e.target.value})} required />
          <Input label="Last Name" value={form.lastName} onChange={e=>setForm({...form,lastName:e.target.value})} required />
          <Input label="Phone" value={form.phone} onChange={e=>setForm({...form,phone:e.target.value})} required />
          <Input label="Email" value={form.email} onChange={e=>setForm({...form,email:e.target.value})} />
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Membership Tier</label>
            <select value={form.membershipTier} onChange={e=>setForm({...form,membershipTier:e.target.value})} className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">
              {Object.entries(MEMBERSHIP_TIER_LABELS).map(([k,v])=><option key={k} value={k}>{v}</option>)}</select></div>
          <div className="sm:col-span-2"><Input label="Address" value={form.address} onChange={e=>setForm({...form,address:e.target.value})} /></div>
        </div>
      </Modal>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════
// Bets
// ═══════════════════════════════════════════════════════════════

export function ArenaBetPage() {
  const [bets, setBets] = useState<any[]>([])
  const [members, setMembers] = useState<any[]>([])
  const [matches, setMatches] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState({ matchId: '', memberId: '', betType: 'fighter1', fighterId: '', amount: 0 })

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [b, mem, mat] = await Promise.all([
        arenaBetRepo.findMany({ page: 1, pageSize: 500, sort: [{ field: 'createdAt', direction: 'desc' }] }),
        arenaMemberRepo.findMany({ page: 1, pageSize: 500 }),
        arenaMatchRepo.findMany({ page: 1, pageSize: 100 }),
      ])
      setBets('items' in b ? b.items : [])
      setMembers('items' in mem ? mem.items : [])
      setMatches('items' in mat ? mat.items : [])
    } catch (e) { console.error(e) } finally { setLoading(false) }
  }, [])
  useEffect(() => { load() }, [load])

  async function placeBet() {
    setSaving(true)
    try {
      const match = matches.find((m: any) => m.id === form.matchId)
      if (!match || match.status !== 'scheduled') { alert('Match not available for betting'); setSaving(false); return }
      const member = members.find((m: any) => m.id === form.memberId)
      const fighter = form.betType === 'fighter1' ? match.fighter1Name : form.betType === 'fighter2' ? match.fighter2Name : 'Draw'
      const odds = form.betType === 'fighter1' ? match.fighter1Odds : form.betType === 'fighter2' ? match.fighter2Odds : match.drawOdds
      const potentialPayout = Math.round(form.amount * odds * 100) / 100

      await arenaBetRepo.create({
        matchId: form.matchId, matchCode: match.matchCode,
        memberId: form.memberId, memberName: member?.fullName ?? '',
        fighterId: form.betType === 'draw' ? 'draw' : (form.betType === 'fighter1' ? match.fighter1Id : match.fighter2Id),
        fighterName: fighter, betType: form.betType,
        amount: form.amount, odds, potentialPayout, status: 'placed', placedAt: Date.now(),
        tenantId: 'default', createdBy: 'admin', updatedBy: 'admin',
      } as any)
      setShowModal(false); setForm({ matchId: '', memberId: '', betType: 'fighter1', fighterId: '', amount: 0 }); await load()
    } catch (e) { console.error(e) } finally { setSaving(false) }
  }

  const totalWagered = bets.reduce((s: number, b: any) => s + (b.amount ?? 0), 0)
  const totalWon = bets.filter((b: any) => b.status === 'won' || b.status === 'paid').reduce((s: number, b: any) => s + (b.actualPayout ?? 0), 0)

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <div><h2 className="text-2xl font-bold text-gray-900">Betting</h2><p className="text-sm text-gray-500 mt-1">{bets.length} bets placed</p></div>
        <Button onClick={() => setShowModal(true)} icon={<Coins className="h-4 w-4" />}>Place Bet</Button>
      </div>
      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {[['Total Bets',bets.length,'blue'],['Total Wagered',`₱${totalWagered.toLocaleString()}`,'amber'],['Total Won',`₱${totalWon.toLocaleString()}`,'green'],['Pending',bets.filter((b:any)=>b.status==='placed').length,'yellow']].map(([l,v,c]:any)=>(
          <div key={l} className={`rounded-xl border border-${c}-200 bg-${c}-50 p-4`}><p className={`text-xs text-${c}-600`}>{l}</p><p className={`mt-1 text-xl font-bold text-${c}-700`}>{v}</p></div>
        ))}
      </div>
      <Card><CardHeader title="Bet History" />
        <div className="overflow-x-auto"><table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50"><tr>{['Member','Match','On','Amount','Odds','Payout','Status'].map(h=><th key={h} className="px-3 py-2.5 text-left text-xs font-medium uppercase text-gray-500">{h}</th>)}</tr></thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {loading ? <tr><td colSpan={7} className="py-8 text-center text-gray-400">Loading...</td></tr> :
             bets.length === 0 ? <tr><td colSpan={7} className="py-8 text-center text-gray-400">No bets placed yet.</td></tr> :
             bets.map((b: any) => (
              <tr key={b.id} className="hover:bg-gray-50">
                <td className="px-3 py-2.5 text-xs font-medium">{b.memberName ?? b.memberId?.slice(0,8)}</td>
                <td className="px-3 py-2.5 text-xs text-gray-500">{b.matchCode ?? b.matchId?.slice(0,8)}</td>
                <td className="px-3 py-2.5 text-xs">{BET_TYPE_LABELS[b.betType as keyof typeof BET_TYPE_LABELS]??b.betType}</td>
                <td className="px-3 py-2.5 font-medium text-right">₱{(b.amount??0).toLocaleString()}</td>
                <td className="px-3 py-2.5 text-blue-600">{b.odds?.toFixed(2)}</td>
                <td className="px-3 py-2.5 text-right font-medium">₱{((b.actualPayout??b.potentialPayout??0)).toLocaleString()}</td>
                <td className="px-3 py-2.5"><Badge color={b.status==='won'||b.status==='paid'?'green':b.status==='lost'?'red':'yellow'}>{BET_STATUS_LABELS[b.status as keyof typeof BET_STATUS_LABELS]??b.status}</Badge></td>
              </tr>
            ))}
          </tbody>
        </table></div>
      </Card>
      <Modal open={showModal} onClose={()=>setShowModal(false)} title="Place Bet" size="md"
        footer={<div className="flex gap-2"><Button variant="secondary" onClick={()=>setShowModal(false)}>Cancel</Button><Button onClick={placeBet} loading={saving} icon={<Coins className="h-4 w-4"/>}>Place Bet</Button></div>}>
        <div className="grid grid-cols-1 gap-3">
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Member</label>
            <select value={form.memberId} onChange={e=>setForm({...form,memberId:e.target.value})} className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">
              <option value="">Select...</option>{members.map((m:any)=><option key={m.id} value={m.id}>{m.fullName}</option>)}</select></div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Match</label>
            <select value={form.matchId} onChange={e=>setForm({...form,matchId:e.target.value})} className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">
              <option value="">Select...</option>{matches.filter((m:any)=>m.status==='scheduled').map((m:any)=><option key={m.id} value={m.id}>{m.matchCode} — {m.fighter1Name} vs {m.fighter2Name}</option>)}</select></div>
          <div><label className="block text-sm font-medium text-gray-700 mb-1">Bet On</label>
            <select value={form.betType} onChange={e=>setForm({...form,betType:e.target.value})} className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">
              {Object.entries(BET_TYPE_LABELS).map(([k,v])=><option key={k} value={k}>{v}</option>)}</select></div>
          <Input label="Amount (₱)" type="number" value={String(form.amount)} onChange={e=>setForm({...form,amount:Number(e.target.value)})} />
        </div>
      </Modal>
    </div>
  )
}
